import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  ShoppingCart,
  Package,
  Monitor,
  FileText,
  ClipboardList,
  ChevronLeft,
  Bell,
  Search,
  ChevronRight,
  LogOut,
  CalendarDays,
  Building2,
  ShieldCheck,
  Database,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { useAuth } from '@/auth/AuthProvider'

interface MenuItem {
  path: string
  icon: typeof LayoutDashboard
  label: string
  children?: Array<{ path: string; label: string }>
  adminOnly?: boolean
}

const menuItems: MenuItem[] = [
  { path: '/dashboard', icon: LayoutDashboard, label: '工作台' },
  {
    path: '/basic-information',
    icon: Database,
    label: '基础信息',
    children: [
      { path: '/basic-information/brands', label: '器械品牌' },
      { path: '/basic-information/medical-device-catalogs', label: '器械分类' },
      { path: '/basic-information/suppliers', label: '供应商' },
    ],
  },
  { path: '/customers', icon: Building2, label: '客户管理' },
  {
    path: '/work-orders',
    icon: ClipboardList,
    label: '工单管理',
    children: [
      { path: '/work-orders', label: '维修工单' },
      { path: '/maintenance-orders', label: '维保工单' },
    ],
  },
  {
    path: '/sales',
    icon: FileText,
    label: '销售',
    children: [
      { path: '/sales/quotes', label: '报价单' },
      { path: '/sales/negotiations', label: '洽谈记录' },
    ],
  },
  {
    path: '/supply-chain/purchase',
    icon: ShoppingCart,
    label: '采购管理',
    children: [
      { path: '/supply-chain/purchase/workbench', label: '采购工作台' },
      { path: '/supply-chain/purchase/list', label: '采购订单' },
    ],
  },
  {
    path: '/supply-chain/inventory',
    icon: Package,
    label: '配件库存',
    children: [
      { path: '/supply-chain/parts', label: '配件信息' },
      { path: '/supply-chain/inventory/list', label: '库存总览' },
      { path: '/supply-chain/inventory/alert', label: '库存预警' },
    ],
  },
  { path: '/supply-chain/device/list', icon: Monitor, label: '设备管理' },
  { path: '/maintenance/calendar', icon: CalendarDays, label: '维保计划' },
  {
    path: '/system',
    icon: ShieldCheck,
    label: '系统管理',
    adminOnly: true,
    children: [
      { path: '/system/employees', label: '员工信息' },
      { path: '/system/roles', label: '角色' },
      { path: '/system/permissions', label: '权限' },
      { path: '/system/dictionaries', label: '数据字典' },
    ],
  },
]

const ROLE_LABEL: Record<string, string> = {
  ADMIN: '管理员',
  STATION_MANAGER: '负责人',
  ENGINEER: '工程师',
  SALES: '销售',
  PROCUREMENT: '采购',
}

export default function MainLayout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { session, logout } = useAuth()
  const [expandedMenus, setExpandedMenus] = useState<Set<string>>(
    () =>
      new Set(
        menuItems
          .filter(
            (item) =>
              item.children &&
              (location.pathname.startsWith(item.path) ||
                item.children.some((child) => location.pathname.startsWith(child.path)))
          )
          .map((item) => item.path)
      )
  )

  const initials = useMemo(() => {
    if (!session?.userName) return 'U'
    return session.userName.trim().slice(0, 1).toUpperCase()
  }, [session?.userName])

  const toggleMenu = (path: string) => {
    setExpandedMenus((prev) => {
      const next = new Set(prev)
      if (next.has(path)) next.delete(path)
      else next.add(path)
      return next
    })
  }

  const isActive = (path: string) => location.pathname.startsWith(path)
  const isParentActive = (item: MenuItem) =>
    item.children?.some((child) => location.pathname.startsWith(child.path)) ?? false
  const visibleMenuItems = menuItems.filter((item) => !item.adminOnly || session?.role === 'ADMIN')

  return (
    <div className="flex min-h-screen bg-gray-50">
      <aside className="flex w-64 flex-col border-r border-gray-200 bg-white">
        <div className="flex h-16 items-center border-b border-gray-200 px-6">
          <div className="mr-3 flex h-8 w-8 items-center justify-center rounded-lg bg-primary-500">
            <span className="font-bold text-white">M</span>
          </div>
          <span className="font-bold text-gray-900">北京恒誉泰医疗科技有限公司</span>
        </div>

        <nav className="flex-1 space-y-1 p-4">
          {visibleMenuItems.map((item) => {
            const Icon = item.icon
            const hasChildren = !!item.children?.length
            const isExpanded = expandedMenus.has(item.path)
            const itemActive = isActive(item.path) || isParentActive(item)

            return (
              <div key={item.path}>
                <button
                  onClick={() => {
                    if (hasChildren) toggleMenu(item.path)
                    else navigate(item.path)
                  }}
                  className={`flex w-full items-center justify-between gap-3 rounded-xl px-4 py-3 transition ${
                    itemActive
                      ? 'bg-primary-50 font-medium text-primary-600'
                      : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className="h-5 w-5" />
                    <span>{item.label}</span>
                  </div>
                  {hasChildren ? (
                    <ChevronRight
                      className={`h-4 w-4 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    />
                  ) : null}
                </button>

                {hasChildren && isExpanded ? (
                  <div className="ml-8 mt-1 space-y-1">
                    {item.children!.map((child) => (
                      <Link
                        key={child.path}
                        to={child.path}
                        className={`block rounded-lg py-2 pl-11 pr-4 text-sm transition ${
                          location.pathname.startsWith(child.path)
                            ? 'bg-primary-50 font-medium text-primary-600'
                            : 'text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </div>
                ) : null}
              </div>
            )
          })}
        </nav>

        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-500 font-bold text-white">
              {initials}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium text-gray-900">{session?.userName ?? '未登录'}</div>
              <div className="truncate text-xs text-gray-500">
                {ROLE_LABEL[session?.role ?? ''] ?? session?.role ?? '-'} · {session?.userId ?? '-'}
              </div>
            </div>
            <button
              onClick={logout}
              className="rounded-lg p-2 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
              title="退出登录"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </aside>

      <div className="flex flex-1 flex-col">
        <header className="flex h-16 items-center justify-between border-b border-gray-200 bg-white px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="rounded-lg p-2 transition hover:bg-gray-100"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">医疗设备维修管理系统</h1>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="搜索配件、采购单、设备..."
                className="w-64 rounded-xl border border-gray-200 py-2 pl-10 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>
            <button className="relative rounded-xl p-2 transition hover:bg-gray-100">
              <Bell className="h-5 w-5 text-gray-600" />
              <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-red-500" />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>
    </div>
  )
}
